<template>
    <section aria-label="Account health" class="flex-none px-1.5 py-2">
        <div v-if="error" class="rounded-lg border border-slate-200 p-2 text-xs text-slate-500" role="status">
            <span v-if="!collapsed">Health unavailable.</span>
            <button type="button" class="text-blue-600 hover:underline" @click="refresh()">Retry</button>
        </div>
        <p v-else-if="!data" class="text-[11px] text-slate-400" role="status">Loading…</p>
        <div v-else class="space-y-2">
            <NuxtLink v-for="group in groups" :key="group.label" :to="group.to"
                class="group block overflow-hidden rounded-lg border border-slate-200 bg-white transition-colors hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                :aria-label="`${group.label}: ${group.description}`" :title="group.description">
                <div class="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50/70 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700"
                    :class="collapsed ? 'justify-center px-1' : ''">
                    <UIcon :name="group.icon" class="h-3.5 w-3.5 flex-none text-slate-500" />
                    <span v-if="!collapsed">{{ group.label }}</span>
                    <UIcon v-if="!collapsed" name="i-ion-chevron-forward" class="ml-auto h-3 w-3 text-slate-400 group-hover:text-slate-600" />
                </div>
                <div class="space-y-1 px-2.5 py-2" :class="collapsed ? 'px-1.5' : ''">
                    <div v-for="status in group.statuses" :key="status.label"
                        :title="`${status.count} ${status.label.toLowerCase()}`" class="flex items-center gap-1.5 text-[11px] leading-4">
                        <UIcon :name="status.icon" :class="status.color" class="h-3 w-3 flex-none" />
                        <span v-if="!collapsed" class="text-slate-500">{{ status.label }}</span>
                        <span class="ml-auto font-semibold tabular-nums" :class="status.count ? 'text-slate-700' : 'text-slate-400'">{{ status.count }}</span>
                    </div>
                </div>
            </NuxtLink>
        </div>
    </section>
</template>

<script setup lang="ts">
defineProps<{ collapsed: boolean }>();
const route = useRoute();
const { data, error, refresh } = useFetch('/api/account-health', { server: false });
const healthy = (count: number) => ({ label: 'Healthy', count, icon: 'i-ion-checkmark-circle', color: 'text-emerald-700' });
const disconnected = (count: number) => ({ label: 'Disconnected', count, icon: 'i-ion-close-circle', color: count ? 'text-red-600' : 'text-slate-400' });
const groups = computed(() => {
    if (!data.value) return [];
    const { mailbox, linkedin } = data.value;
    return [
        {
            label: 'Mailboxes', icon: 'i-ion-mail-outline', to: '/settings/mailbox',
            description: `${mailbox.healthy} healthy, ${mailbox.disconnected} disconnected, ${mailbox.highBounce} high bounce, ${mailbox.unknown} unknown. Healthy means connected with no high-bounce warning. High bounce means over 3% with at least 10 sequenced contacts and can overlap disconnected or unknown. Based on last observed connection status.`,
            statuses: [healthy(mailbox.healthy), disconnected(mailbox.disconnected),
                { label: 'High bounce', count: mailbox.highBounce, icon: 'i-ion-warning', color: mailbox.highBounce ? 'text-amber-600' : 'text-slate-400' },
                ...(mailbox.unknown ? [{ label: 'Unknown', count: mailbox.unknown, icon: 'i-ion-help-circle', color: 'text-slate-500' }] : []),
            ],
        },
        {
            label: 'LinkedIn', icon: 'i-ion-logo-linkedin', to: '/settings/linkedin',
            description: `${linkedin.healthy} healthy, ${linkedin.disconnected} disconnected, ${linkedin.warming} warming. Healthy means connected. Disconnected includes connection errors. Temporary sending limits are not included.`,
            statuses: [healthy(linkedin.healthy), disconnected(linkedin.disconnected),
                ...(linkedin.warming ? [{ label: 'Warming', count: linkedin.warming, icon: 'i-ion-time-outline', color: 'text-amber-600' }] : []),
            ],
        },
    ];
});
watch(() => route.path, () => { void refresh(); });
let interval: ReturnType<typeof setInterval>;
function refreshOnFocus() { void refresh(); }
onMounted(() => {
    interval = setInterval(() => { if (!document.hidden) void refresh(); }, 300_000);
    window.addEventListener('focus', refreshOnFocus);
});
onBeforeUnmount(() => {
    clearInterval(interval);
    window.removeEventListener('focus', refreshOnFocus);
});
</script>
